import type { FormValues } from "@/widgets/classic-form/form";
import type { EventFieldsGroup } from "@/entities/form-metadata";
import { EventAssertion, getRowEventIdentities } from "@/entities/form-metadata";
import { resolveEventKey } from "./resolveEventKey";
import { seedInstanceSlots } from "./seedInstanceSlots";

export const populateEventFields = ({
    fieldGroup,
    dataModel,
    formValuesEventsData,
    isEditForm,
    matchAllTermsExactly,
    defaultYearProximity = null,
}: {
    fieldGroup: EventFieldsGroup;
    dataModel: FormValues;
    formValuesEventsData: FormValues["events"];
    isEditForm?: boolean;
    matchAllTermsExactly: boolean;
    defaultYearProximity?: number | null;
}) => {
    const { events: eventsFromDataModel } = dataModel;
    const { fieldsetDisplayStrategy } = fieldGroup;
    const prefilled = fieldsetDisplayStrategy !== "empty-dynamic";
    const removable = fieldsetDisplayStrategy !== "prefilled-static";
    if (!isEditForm && !prefilled && removable) return;

    fieldGroup.fieldsets.forEach((event) => {
        const { isYearExactnessEnabled, isPlaceExactnessEnabled } = event.fields.reduce(
            (acc, field) => {
                const {
                    assertion,
                    features: { showExactness },
                } = field;
                if (assertion === EventAssertion.Year) {
                    acc.isYearExactnessEnabled = showExactness ?? true;
                } else if (assertion === EventAssertion.Place) {
                    acc.isPlaceExactnessEnabled = showExactness ?? true;
                }
                return acc;
            },
            { isYearExactnessEnabled: true, isPlaceExactnessEnabled: true },
        );

        // Seed every distinct event identity the fieldset's fields carry (e.g. a "Death" row whose
        // place field is Burial), not the fieldset's own deprecated identity, so each field's binding has a state entry to write to.
        const identitiesToSeed = getRowEventIdentities(event);

        const seedsEmptyRow = !removable || prefilled;

        // Taken across every identity the fieldset touches, so a row whose date and place name different
        // events gets a slot in both entries instead of leaving the shorter entry's input with nothing to write into.
        const rowCount = Math.max(
            seedsEmptyRow ? 1 : 0,
            ...identitiesToSeed.map((identity) => {
                const eventKey = resolveEventKey(identity);
                return eventKey ? (eventsFromDataModel[eventKey]?.instances.length ?? 0) : 0;
            }),
        );

        identitiesToSeed.forEach((identity) => {
            const eventKey = resolveEventKey(identity);
            if (!eventKey) return;
            const dataModelEvent = eventsFromDataModel[eventKey];
            const entry = formValuesEventsData[eventKey];

            if (entry) {
                if (entry.relation !== identity.relation) entry.relation = identity.relation;
                if (entry.eventName !== identity.eventType) entry.eventName = identity.eventType ?? "";
            } else {
                formValuesEventsData[eventKey] = {
                    eventName: identity.eventType ?? "",
                    instances: [],
                    relation: identity.relation,
                };
            }

            seedInstanceSlots({
                formValuesEventsData,
                eventKey,
                dataModelEvent,
                rowCount,
                isYearExactnessEnabled,
                isPlaceExactnessEnabled,
                matchAllTermsExactly,
                isEditForm: isEditForm ?? false,
                defaultYearProximity,
            });
        });
    });
};
